import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring, useInView, AnimatePresence } from "framer-motion";
import { Zap, Activity, Users, Rocket, Globe, Shield, Brain, Target, ArrowRight, MousePointer2, Timer, Maximize2, HeartPulse, Quote, Fingerprint } from "lucide-react";
import { Link } from "react-router-dom";

// --- Assets & Data ---
const stories = [
  {
    id: 1,
    year: "2021",
    date: "THE ORIGIN",
    title: "The Zero Point",
    content: "FOZZIL was planted in the mind of the Founder. A transition from student to visionary. It began not as a company, but as a question: 'Can we control the digital world with our voice?'",
    icon: <Zap className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 2,
    year: "2022",
    date: "RESEARCH",
    title: "Project N.I.S.T.I.A",
    content: "The abstract became concrete. N.I.S.T.I.A for the visually impaired. Then T.A.E.L.S for healthcare. We failed fast, learned faster. These were the prototypes of our ambition.",
    icon: <Activity className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 3,
    year: "2023",
    date: "IGNITION",
    title: "The Pivot",
    content: "March 2023. The catalyst event. Mentorships, workshops, and the meeting with a visionary guide. The realization that a 'project' generates interest, but a 'startup' generates impact.",
    icon: <Users className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 4,
    year: "2023",
    date: "ACCELERATION",
    title: "Aerospace Mentorship",
    content: "Under the guidance of aerospace pioneers at a leading space research agency. We didn't just learn coding; we learned engineering discipline. The amateur mindset was left behind.",
    icon: <Rocket className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 5,
    year: "2023",
    date: "LAUNCH",
    title: "FOZZIL Public",
    content: "The stealth mode ended. The professional identity emerged. Vision: 'Train human intelligence to work smarter.' Mission: 'Empower efficiency through adaptive AI.'",
    icon: <Globe className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 6,
    year: "CORE",
    date: "IDENTITY",
    title: "The Invisible Force",
    content: "FOZZIL is not just a brand; it's an operating system for innovation. We are the invisible layer that makes the complex feel simple and the impossible feel inevitable. S.H.A.D.E.",
    icon: <Shield className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 7,
    year: "AI",
    date: "SENIENCE",
    title: "IQ + EQ",
    content: "Intelligence without empathy is just calculation. We are building AI that understands not just your commands, but your intent, your context, and your humor.",
    icon: <Brain className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  },
  {
    id: 8,
    year: "FUTURE",
    date: "VISION",
    title: "Intelligence At Your Command!",
    content: "We are writing the future. A future where technology recedes into the background, and human potential comes to the foreground. Intelligence, always at your command.",
    icon: <Target className="w-full h-full text-blue-500" />,
    color: "#3B82F6", // Blue
    bg: "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.15) 0%, rgba(0,0,0,1) 70%)"
  }
];

// --- Components ---

const GlitchText = ({ text }) => {
  return (
    <div className="relative inline-block group">
      <span className="relative z-10">{text}</span>
      <span className="absolute top-0 left-0 -z-10 w-full h-full text-red-500 opacity-0 group-hover:opacity-70 group-hover:animate-pulse group-hover:translate-x-[2px]">{text}</span>
      <span className="absolute top-0 left-0 -z-10 w-full h-full text-blue-500 opacity-0 group-hover:opacity-70 group-hover:animate-pulse group-hover:-translate-x-[2px]">{text}</span>
    </div>
  );
};

const CinematicCard = ({ item, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: "-20% 0px -20% 0px", amount: 0.6 });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);
  const x = useTransform(scrollYProgress, [0, 1], [index % 2 === 0 ? -50 : 50, index % 2 === 0 ? 50 : -50]);
  
  return (
    <motion.div 
      ref={ref}
      style={{ scale, opacity }}
      className={`min-h-[80vh] w-full flex items-center justify-center relative perspective-[1000px]`}
    >
      {/* Background Ambience */}
      <motion.div 
        animate={{ opacity: isInView ? 1 : 0 }}
        transition={{ duration: 1 }}
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: item.bg }}
      />
      
      <div className={`relative z-10 max-w-7xl w-full grid grid-cols-1 md:grid-cols-2 gap-12 items-center px-6 ${index % 2 !== 0 ? 'md:grid-flow-dense' : ''}`}>
        
        {/* Visual Side: 3D Animated Card */}
        <motion.div
           className={`${index % 2 !== 0 ? 'md:col-start-2' : ''} flex justify-center`}
           style={{ x: useTransform(scrollYProgress, [0, 1], [0, index % 2 === 0 ? 100 : -100]) }}
        >
            <div className="relative w-64 h-64 sm:w-80 sm:h-80 md:w-[28rem] md:h-[28rem] perspective-[2000px] group">
               {/* Spinning Rings HUD */}
               <motion.div 
                 animate={{ rotate: 360 }}
                 transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                 className="absolute inset-[-15%] border border-dashed border-white/20 rounded-full"
               />
               <motion.div 
                 animate={{ rotate: -360 }}
                 transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                 className="absolute inset-[-8%] border border-white/10 rounded-full"
               />

               {/* The Cube / Card */}
               <motion.div
                  whileHover={{ rotateX: 10, rotateY: 10 }}
                  className="w-full h-full bg-black/40 backdrop-blur-xl border border-white/10 rounded-[2rem] flex items-center justify-center relative overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)]"
               >
                   <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-50" />
                   
                   <motion.div 
                     className="relative z-10 w-1/3 h-1/3 sm:w-1/2 sm:h-1/2"
                     whileHover={{ scale: 1.1, filter: "brightness(1.5) drop-shadow(0 0 20px currentColor)" }}
                     style={{ color: item.color }}
                   >
                      {item.icon}
                   </motion.div>

                   <div className="absolute top-6 left-6 sm:top-8 sm:left-8 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white/50 rounded-full" />
                   <div className="absolute top-6 right-6 sm:top-8 sm:right-8 w-8 sm:w-12 h-[1px] bg-white/30" />
                   <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 font-mono text-[8px] sm:text-[10px] text-white/30 tracking-[0.2em]">
                     ID: {item.id.toString().padStart(3, '0')}
                   </div>
               </motion.div>
            </div>
        </motion.div>

        {/* Content Side: Cinematic Text Reveal */}
        <div className={`${index % 2 !== 0 ? 'md:col-start-1 md:text-right text-left' : 'text-left'} space-y-4 sm:space-y-6 order-2 md:order-none mt-8 md:mt-0`}>
           <motion.div
             initial={{ opacity: 0, x: -30 }}
             whileInView={{ opacity: 1, x: 0 }}
             transition={{ duration: 0.8, ease: "circOut" }}
           >
              <div className={`flex items-center gap-4 mb-2 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                 <span className="w-8 md:w-12 h-[2px]" style={{ backgroundColor: item.color }} />
                 <span className="font-mono text-xs md:text-sm tracking-[0.4em] uppercase" style={{ color: item.color }}>
                   {item.date}
                 </span>
              </div>
              
              <h2 className={`text-6xl md:text-8xl lg:text-9xl font-black text-white/5 absolute -z-10 -translate-y-12 md:-translate-y-16 select-none font-sans hidden sm:block ${index % 2 !== 0 ? 'md:right-0' : 'left-0'}`}>
                {item.year}
              </h2>
              
              <h3 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6 relative">
                 <GlitchText text={item.title} />
              </h3>
              
              <p className={`text-base sm:text-xl md:text-2xl text-gray-400 font-light leading-relaxed border-white/10 pl-4 sm:pl-6 ${index % 2 !== 0 ? 'md:border-r-2 md:border-l-0 md:pl-0 md:pr-6 border-l-2' : 'border-l-2'}`}>
                 {item.content}
              </p>
           </motion.div>
        </div>

      </div>
    </motion.div>
  );
}

const StoriesCompany = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);

  return (
    <div className="bg-black min-h-screen text-white font-sans selection:bg-white/20 overflow-x-hidden">
      
      {/* HUD Overlay Lines */}
      <div className="fixed inset-0 pointer-events-none z-50 mix-blend-overlay opacity-20 hidden md:block">
         <div className="absolute top-10 left-10 w-40 h-[1px] bg-white" />
         <div className="absolute bottom-10 right-10 w-40 h-[1px] bg-white" />
         <div className="absolute top-10 right-10 w-[1px] h-40 bg-white" />
         <div className="absolute bottom-10 left-10 w-[1px] h-40 bg-white" />
      </div>

      {/* Progress Bar (Top) */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 z-50"
        style={{ scaleX }}
      />

      {/* Hero Section: The Portal -> Reverted */}
      <section className="h-[120vh] relative flex items-center justify-center overflow-hidden">
         <motion.div 
            style={{ y: useTransform(scrollYProgress, [0, 1], [0, -300]) }}
            className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-900 via-black to-black" 
         />
         
         <div className="z-10 text-center px-4 sm:px-6 max-w-6xl mt-[-10vh] md:mt-[-20vh]">
            <motion.h1 
               initial={{ opacity: 0, y: 50, scale: 0.95 }}
               animate={{ opacity: 1, y: 0, scale: 1 }}
               transition={{ duration: 1.2, ease: "easeOut" }}
               className="text-5xl sm:text-7xl md:text-8xl lg:text-[10rem] font-bold tracking-tighter text-white mb-6 leading-tight sm:leading-none"
            >
               THE COMPANY
            </motion.h1>
            
            <motion.p 
               initial={{ opacity: 0 }} 
               animate={{ opacity: 1 }}
               transition={{ delay: 0.5, duration: 1 }}
               className="text-lg sm:text-2xl md:text-3xl lg:text-4xl text-gray-400 font-light max-w-3xl mx-auto leading-relaxed"
            >
               Evolution through intelligence.
            </motion.p>
         </div>

         <motion.div 
            style={{ 
               opacity: useTransform(scrollYProgress, [0, 0.2], [1, 0]),
               scale: useTransform(scrollYProgress, [0, 0.2], [1, 0.8])
            }}
            className="absolute bottom-20 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 text-gray-500"
         >
            <span className="text-sm tracking-[0.3em] uppercase">Scroll</span>
            <div className="w-px h-16 bg-gradient-to-b from-gray-500 to-transparent" />
         </motion.div>
      </section>

      {/* The Timeline Stream */}
      <div className="relative z-10">
         {stories.map((story, index) => (
           <CinematicCard key={story.id} item={story} index={index} />
         ))}
      </div>

      {/* Philosophy Section - Defining the Acronym */}
      <section className="py-32 bg-black relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 relative z-10">
              <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="text-center mb-24"
              >
                  <h2 className="text-xs sm:text-sm font-bold text-blue-500 tracking-[0.4em] uppercase mb-4">The Philosophy</h2>
                  <h3 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white tracking-tighter uppercase">The FOZZIL Philosophy</h3>
                  <p className="text-xl md:text-2xl text-gray-400 font-light mt-8 max-w-4xl mx-auto italic">
                    "We don’t just build technology; we curate the environments where intelligence lives."
                  </p>
                  <p className="text-lg md:text-2xl mt-6 font-mono tracking-[0.2em] text-blue-500">
                    <span className="text-white border-b-2 border-blue-500">F</span>uture <span className="text-white border-b-2 border-blue-500">O</span>riented <span className="text-white border-b-2 border-blue-500">Z</span>one-of <span className="text-white border-b-2 border-blue-500">I</span>ntelligent <span className="text-white border-b-2 border-blue-500">L</span>ife
                  </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                  {[
                      {
                          icon: <Timer className="w-8 h-8" />,
                          title: "Future Oriented",
                          subtitle: "50-Year Horizon",
                          color: "text-blue-500",
                          bgColor: "bg-blue-500/10",
                          hoverBorder: "hover:border-blue-500/30",
                          content: "We operate on a 50-year horizon. Our architecture is designed to be \"evergreen,\" ensuring that as the world changes, our foundations remain unshakable."
                      },
                      {
                          icon: <Maximize2 className="w-8 h-8" />,
                          title: "Zone-of",
                          subtitle: "Defining Spaces",
                          color: "text-sky-500",
                          bgColor: "bg-sky-500/10",
                          hoverBorder: "hover:border-sky-500/30",
                          content: "We define physical and digital spaces—from local environments to global networks—where every interaction is optimized, seamless, and secure."
                      },
                      {
                          icon: <HeartPulse className="w-8 h-8" />,
                          title: "Intelligent Life",
                          subtitle: "Human Centric",
                          color: "text-indigo-500",
                          bgColor: "bg-indigo-500/10",
                          hoverBorder: "hover:border-indigo-500/30",
                          content: "At the center of every algorithm is a human being. Our goal is to enhance the quality of life through T.A.E.L.S, creating a symbiosis between human intuition and machine precision."
                      }
                  ].map((layer, i) => (
                      <motion.div 
                          key={i}
                          initial={{ opacity: 0, y: 50 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.2, duration: 0.8 }}
                          className={`group p-8 rounded-[2.5rem] bg-gradient-to-br from-black via-black to-blue-900/20 border border-blue-500/10 ${layer.hoverBorder} transition-all duration-500 shadow-2xl shadow-blue-900/10`}
                      >
                          <div className={`w-16 h-16 rounded-[32px] ${layer.bgColor} flex items-center justify-center ${layer.color} mb-8 group-hover:scale-110 transition-transform`}>
                              {layer.icon}
                          </div>
                          <h4 className="text-3xl font-bold text-white mb-2">{layer.title}</h4>
                          <p className={`${layer.color} text-sm font-mono tracking-widest uppercase mb-6`}>{layer.subtitle}</p>
                          <p className="text-gray-400 leading-relaxed font-light">
                              {layer.content}
                          </p>
                      </motion.div>
                  ))}
              </div>

              {/* Concept to Ecosystem Section */}
              <motion.div 
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1.5 }}
                  className="mt-16 sm:mt-32 p-6 sm:p-12 rounded-[2rem] sm:rounded-[3rem] bg-gradient-to-br from-black via-black to-blue-900/20 border border-blue-500/10 shadow-2xl shadow-blue-950/20 space-y-12"
              >
                  <div className="flex flex-col md:grid md:grid-cols-[auto_1fr] items-center md:items-start gap-8 sm:gap-12">
                      <div className="w-24 h-24 sm:w-32 sm:h-32 shrink-0 bg-black border border-white/10 rounded-[32px] sm:rounded-[32px] flex items-center justify-center shadow-[0_0_50px_rgba(37,99,235,0.4)]">
                          <img src="/logoicon.png" alt="FOZZIL Logo" className="w-24 h-24 sm:w-30 sm:h-30 object-contain" />
                      </div>
                      <div className="text-center md:text-left">
                          <h5 className="text-xl sm:text-3xl font-bold text-white mb-4 uppercase tracking-tight">From Concept to Ecosystem</h5>
                          <p className="text-gray-400 text-base sm:text-lg lg:text-xl font-light leading-relaxed max-w-4xl">
                              Our journey began with a challenge: <span className="text-white italic">How do we make complex AI feel human?</span> This led to our first major milestone—the <span className="text-blue-400 font-medium uppercase tracking-wider">AI Museum</span>. By creating a living "Interaction Lab," we proved that a <span className="text-indigo-400 font-medium italic">Zone-of Intelligent Life</span> could transform how people learn, work, and interact with the digital world.
                          </p>
                          <p className="text-gray-400 text-base sm:text-lg lg:text-xl font-light leading-relaxed max-w-4xl mt-6">
                              The heart of this ecosystem is <span className="text-white font-bold tracking-widest">T.A.E.L.S</span>. More than just a tool, it is the cognitive engine that powers every "Zone" we create. It is designed to be proactive, adaptive, and—above all—at your command.
                          </p>
                      </div>
                  </div>

                  <div className="h-px w-full bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />

                  <div className="flex flex-col md:grid md:grid-cols-[1fr_auto] items-center md:items-start gap-8 sm:gap-12">
                      <div className="text-center md:text-right order-2 md:order-1">
                          <h5 className="text-xl sm:text-3xl font-bold text-white mb-4 uppercase tracking-tight">The FOZZIL ID</h5>
                          <p className="text-gray-400 text-base sm:text-lg lg:text-xl font-light leading-relaxed max-w-4xl ml-auto">
                              The heartbeat of our story is the <span className="text-white font-bold tracking-widest">FOZZIL ID</span>. It is the "Key to the Zone," a unified identity that allows users to move through our world with zero friction. It represents our commitment to a future where your preferences and your intelligence follow you wherever you go.
                          </p>
                      </div>
                      <div className="w-24 h-24 sm:w-32 sm:h-32 shrink-0 bg-black border border-white/10 rounded-[32px] sm:rounded-[32px] flex items-center justify-center shadow-[0_0_50px_rgba(37,99,235,0.4)] order-1 md:order-2">
                          <img src="/logoicon.png" alt="FOZZIL Logo" className="w-24 h-24 sm:w-30 sm:h-30 object-contain" />
                      </div>
                  </div>
              </motion.div>
          </div>
      </section>

      {/* Global Ambition Section */}
      <section className="py-32 bg-zinc-950 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
              <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1 }}
              >
                  <h2 className="text-xs sm:text-sm font-bold text-blue-500 tracking-[0.4em] uppercase mb-4">The Next Chapter</h2>
                  <h3 className="text-4xl sm:text-7xl font-bold text-white tracking-tighter uppercase mb-12">Our Global Ambition</h3>
                  <p className="text-xl md:text-3xl text-gray-400 font-light max-w-4xl mx-auto leading-relaxed">
                    FOZZIL is currently transitioning into its next chapter as a <span className="text-blue-500 font-bold">Delaware C-Corp</span>. As we bridge the gap between our roots in India and the global stage, our mission remains the same: to conquer the challenges of tomorrow by building the most intelligent "Zones" on Earth.
                  </p>
              </motion.div>
          </div>
      </section>

      {/* Founder's Note Section */}
      <section className="min-h-screen flex flex-col items-center justify-center relative perspective-[1000px] bg-black py-20 sm:py-32 border-t border-white/5">
         <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(59,130,246,0.05)_0%,_transparent_70%)]" />
         
         <div className="max-w-4xl px-6 relative z-10 text-center">
            <Quote className="w-12 h-12 sm:w-16 sm:h-16 text-blue-500/20 mb-8 sm:mb-12 mx-auto" />
            <motion.div
               initial={{ opacity: 0, y: 30 }}
               whileInView={{ opacity: 1, y: 0 }}
               transition={{ duration: 1 }}
            >
               <h2 className="text-2xl sm:text-3xl md:text-5xl font-medium text-white mb-8 sm:mb-12 leading-tight italic font-serif">
                  "Intelligence At Your Command! Welcome to the Zone."
               </h2>
               <div className="flex flex-col items-center gap-4">
                  <div className="w-8 sm:w-12 h-px bg-blue-500" />
                  <span className="text-lg sm:text-xl font-bold tracking-widest uppercase text-blue-500">RUJAI ANANTH</span>
                  <span className="text-xs sm:text-sm text-gray-500 tracking-widest uppercase">CEO, FOZZIL</span>
               </div>
            </motion.div>
         </div>
      </section>

      {/* The Future - Final CTA */}
      <section className="h-[80vh] sm:h-screen flex items-center justify-center relative perspective-[1000px] bg-black px-6">
         <div className="absolute inset-0 bg-gradient-to-b from-black via-blue-900/10 to-black" />
         
         <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="text-center relative z-10"
         >
            <h2 className="text-4xl sm:text-6xl md:text-8xl lg:text-9xl font-bold mb-6 sm:mb-8 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-white tracking-tighter leading-tight">
               JOIN THE MISSION
            </h2>
            <p className="text-lg sm:text-xl md:text-3xl mb-8 sm:mb-12 max-w-2xl mx-auto font-light leading-relaxed text-gray-400">
               The history is written. The future is being coded. <br className="hidden sm:block"/>
               Are you a spectator or a player?
            </p>
            
            <a 
               href="https://support.fozzil.in/contact"
               className="group relative inline-flex items-center gap-3 px-6 sm:px-10 py-3 sm:py-5 bg-white text-black text-base sm:text-xl font-bold rounded-full hover:bg-blue-600 hover:text-white border-2 border-white hover:border-blue-600 transition-all duration-300 shadow-[0_0_50px_rgba(255,255,255,0.1)]"
            >
               <span className="relative z-10 uppercase tracking-widest">Inquire Now</span>
               <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-2 transition-transform" />
            </a>
         </motion.div>
      </section>

    </div>
  );
};

export default StoriesCompany;
